/* clientFolderSave — orchestrace uložení karty klienta.
 *
 * PROČ EXISTUJE (WS2 QA fix): původní inline `save()` v ClientDetailRaynet neměl
 * try/finally. Když `onPatch` (PUT /api/crm/contacts/:id) selhal — backend down po
 * redeployi, validační 4xx, výpadek sítě — rejektnutá promise:
 *   1) přeskočila `setSaving(false)` → tlačítko „Uložit změny" zůstalo NAVŽDY
 *      disabled se stavem „Ukládám…" (poradci to vypadalo jako zamrznutá app),
 *   2) vyhodila unhandled promise rejection (onClick handler ji nezachytil).
 *
 * FOUNDATION FIX (2026-07): přidán DIRTY-FIELD PATCH.
 *   Dřív `buildContactSavePatch` rozprostřel CELÝ formulář → PUT přepsal i pole,
 *   která uživatel needitoval, a defaulty z initForm (category||"B", country||"Česká
 *   republika"…) mohly přepsat prázdnou DB hodnotu. Nově se s `baseline` posílají
 *   JEN skutečně změněná pole → netknutá pole zůstanou beze změny (backend $set
 *   s exclude_unset je nezmění) a nehrozí přepis defaultem.
 *   Bez `baseline` se chová jako dřív (celý normalizovaný formulář) — zpětná kompat.
 */

/* Pole s BJ (bodovými jednotkami) — prázdná hodnota musí být null, ne NaN. */
const BJ_FIELDS = ["estimate_bj", "signed_bj"];

/* Normalizace formuláře do tvaru, který jde na backend: prázdné BJ → null, jinak Number. */
export function normalizeContactForm(form) {
  const out = { ...form };
  for (const f of BJ_FIELDS) {
    out[f] = form[f] === "" || form[f] == null ? null : Number(form[f]);
  }
  return out;
}

/* Hloubkové porovnání JSON-ovitých hodnot (string/number/bool/null/pole/objekt).
 * Používá se pro detekci změněných polí — bez závislosti na knihovně. */
export function jsonEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b; // null/undefined jen samy se sebou
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => jsonEqual(a[k], b[k]));
  }
  return false;
}

/* Sestaví patch z formuláře karty.
 *  - `baseline` zadán → DIRTY-FIELD PATCH: jen pole odlišná od baseline (bezpečné,
 *    nepřepisuje netknutá pole ani prázdné DB hodnoty defaultem).
 *  - `baseline` vynechán → celý normalizovaný formulář (zpětná kompatibilita). */
export function buildContactSavePatch(form, baseline) {
  const normalized = normalizeContactForm(form);
  if (!baseline) return normalized;
  const baseNorm = normalizeContactForm(baseline);
  const patch = {};
  for (const k of Object.keys(normalized)) {
    if (!jsonEqual(normalized[k], baseNorm[k])) patch[k] = normalized[k];
  }
  return patch;
}

/* Uloží patch a garantuje, že se `setSaving(false)` zavolá i při chybě.
 * onSuccess se spustí JEN při úspěchu (žádný „Uloženo" toast po selhání).
 * Vrací true při úspěchu, false při chybě — volající tím řídí chybový stav.
 * (Prázdný patch neřeší tady — volající ho neposílá, když není „dirty".) */
export async function runContactSave({ onPatch, patch, setSaving, onSuccess }) {
  setSaving(true);
  try {
    await onPatch(patch);
    onSuccess?.();
    return true;
  } catch {
    // rodič už zobrazil toast.error + re-thrownul; spolkneme, aby nevznikla
    // unhandled rejection a aby se nezobrazil chybový toast dvakrát.
    // Volající pozná selhání podle vrácené `false` a nastaví chybový stav.
    return false;
  } finally {
    setSaving(false);
  }
}
